import React from 'react';
import { Icon } from '../../../../core/design-system/components/Icon';
import ClickableAvatar from '../../../../shared/components/ClickableAvatar';
import { formatRelativeDate } from '../../../../shared/utils/date/DateFormatters';
import { getActivityTitle } from '../../../../shared/utils/activity/getActivityTitle';
import './styles.css';
import { useTranslation } from 'react-i18next';
import { useActivityNavigation } from '../../../../shared/hooks/useActivityNavigation';
import { useFollowing } from '../../../profile/hooks/useFollowing';
import { useProfileNavigation } from '../../../profile/hooks/useProfileNavigation';
import { useHomeNavigation } from '../../../../shared/hooks/useHomeNavigation';
import { useNotification } from '../../../../shared/hooks/useNotification';

const NOTIFICATION_TYPES = {
  POST_CREATED: {
    icon: 'message-circle',
    isActivity: false
  },
  ACTIVITY_SHARE: {
    icon: 'notification-bell',
    isActivity: true
  },
  JOIN_ACTIVITY: {
    icon: 'notification-bell',
    isActivity: true
  },
  LEAVE_ACTIVITY: {
    icon: 'notification-bell',
    isActivity: true
  },
  PERSONAL_JOIN_CONFIRMATION: {
    icon: 'user-check',
    isActivity: true
  },
  PERSONAL_LEAVE_CONFIRMATION: {
    icon: 'user-minus',
    isActivity: true
  },
  FOLLOW_REQUEST: {
    icon: 'user-plus',
    isActivity: false,
    isFollowRequest: true
  },
  FOLLOW_REQUEST_ACCEPTED: {
    icon: 'user-check',
    isActivity: false,
    isFollowRequest: true
  },
  FOLLOW_REQUEST_REJECTED: {
    icon: 'user-x',
    isActivity: false,
    isFollowRequest: true
  },
  NEARBY_ACTIVITY: {
    icon: 'map-pin',
    isActivity: true
  },
  NEW_ACTIVITY_NEARBY: {
    icon: 'map-pin',
    isActivity: true
  },
  ACTIVITY_REMINDER: {
    icon: 'bell',
    isActivity: true
  },
  ACTIVITY_REMINDER_2H: {
    icon: 'clock',
    isActivity: true
  },
  ACTIVITY_REMINDER_NEXT_DAY: {
    icon: 'calendar',
    isActivity: true
  },
  ACTIVITY_CANCELLED: {
    icon: 'x-circle',
    isActivity: true
  },
  ONBOARDING: {
    isSystem: true,
    isActivity: false
  },
  PUSH_ACTIVATION: {
    isSystem: true,
    isActivity: false
  }
};

const NotificationItem = ({ notification, onEnablePush, onClick }) => {
  const { t, i18n } = useTranslation('notifications');
  const { type, sender, activity, createdAt, read, content, senderId, status } = notification;
  const { goToActivityDetails } = useActivityNavigation();
  const { goToHome } = useHomeNavigation();
  const { followUser, unfollowUser } = useFollowing(senderId);
  const { handleNavigate: handleSenderNavigate } = useProfileNavigation(sender);
  const notificationConfig = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.ACTIVITY_SHARE;
  const notification_toast = useNotification('notifications');

  const handleClick = () => {
    onClick?.(notification.id);
    if (type === 'ONBOARDING') {
      goToHome();
      return;
    }
    if ((type === 'FOLLOW_REQUEST' || type === 'FOLLOW_REQUEST_ACCEPTED') && handleSenderNavigate) {
      handleSenderNavigate();
      return;
    }
    if (type === 'ACTIVITY_SHARE' && activity?.id) {
      window.location.href = `/dashboard/activity/${activity.id}?share=true`;
      return;
    }
    if (notificationConfig.isActivity && activity?.id) {
      goToActivityDetails(activity.id);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleFollowRequestResponse = async (accepted) => {
    if (!senderId) return;
    
    try {
      if (accepted) {
        await followUser();
        notification_toast.success('follow_request.accepted', { name: sender?.name || 'Utilisateur' });
      } else {
        await unfollowUser();
        notification_toast.info('follow_request.rejected', { name: sender?.name || 'Utilisateur' });
      }
    } catch (error) {
      console.error('Erreur lors du traitement de la demande de follow:', error);
      notification_toast.error('follow_request.error');
    }
  };

  const renderIcon = () => {
    if (notificationConfig.isSystem) {
      return (
        <div className="notification-logo">
          R
        </div>
      );
    }
    return <Icon name={notificationConfig.icon} size={24} />;
  };

  const renderContent = () => {
    const activityTitle = getActivityTitle({ activity, t, i18n });
      
    switch (type) {
      case 'POST_CREATED':
        return (
          <>
            <div className="notification-text">
              {t('types.post_created.title')}
            </div>
            {content && (
              <div className="notification-subtext">
                {t('types.post_created.preview', { content })}
              </div>
            )}
          </>
        );
      case 'ACTIVITY_SHARE':
        return (
          <div className="notification-text clickable-activity">
            {t('types.activity_share', { 
              type: activityTitle
            })}
          </div>
        );
      case 'JOIN_ACTIVITY':
        return (
          <div className="notification-text clickable-activity">
            {t('types.join_activity', { 
              name: sender?.name || 'Quelqu\'un',
              count: activity?.currentParticipants || 0,
              type: activityTitle,
              activityId: activity?.id || ''
            })}
          </div>
        );
      case 'LEAVE_ACTIVITY':
        return (
          <div className="notification-text clickable-activity">
            {t('types.leave_activity', { 
              name: sender?.name || t('fallback.someone'),
              type: activityTitle,
              activityId: activity?.id || ''
            })}
          </div>
        );
      case 'PERSONAL_JOIN_CONFIRMATION':
        return (
          <div className="notification-text clickable-activity">
            {t('types.personal_join_confirmation', { 
              type: activityTitle,
              activityId: activity?.id || ''
            })}
          </div>
        );
      case 'PERSONAL_LEAVE_CONFIRMATION':
        return (
          <div className="notification-text clickable-activity">
            {t('types.personal_leave_confirmation', { 
              type: activityTitle,
              activityId: activity?.id || ''
            })}
          </div>
        );
      case 'FOLLOW_REQUEST':
        return (
          <div className="notification-follow-request">
            <div className="notification-text clickable-activity">
              {t('types.follow_request', { 
                name: sender?.name || t('fallback.someone')
              })}
            </div>
            {status === 'pending' && (
              <div className="follow-request-actions">
                <button 
                  type="button"
                  className="accept-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFollowRequestResponse(true);
                  }}
                >
                  {t('actions.accept')}
                </button>
                <button 
                  type="button"
                  className="reject-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFollowRequestResponse(false);
                  }}
                >
                  {t('actions.reject')}
                </button>
              </div>
            )}
          </div>
        );
      case 'FOLLOW_REQUEST_ACCEPTED':
        return (
          <div className="notification-text clickable-activity">
            {t('types.follow_request_accepted', { 
              name: sender?.name || t('fallback.someone')
            })}
          </div>
        );
      case 'FOLLOW_REQUEST_REJECTED':
        return (
          <div className="notification-text clickable-activity">
            {t('types.follow_request_rejected', { 
              name: sender?.name || t('fallback.someone')
            })}
          </div>
        );
      case 'NEARBY_ACTIVITY':
        return (
          <div className="notification-text clickable-activity">
            {t('types.nearby_activity', { 
              type: activityTitle
            })}
          </div>
        );
      case 'NEW_ACTIVITY_NEARBY':
        return (
          <div className="notification-text clickable-activity">
            {t('types.new_activity_nearby', { 
              type: activityTitle,
              author: sender?.name || t('fallback.someone')
            })}
          </div>
        );
      case 'ACTIVITY_REMINDER':
        return (
          <div className="notification-text clickable-activity">
            {t('types.activity_reminder', { 
              type: activityTitle
            })}
          </div>
        );
      case 'ACTIVITY_REMINDER_2H':
        return (
          <div className="notification-text clickable-activity">
            {t('types.activity_reminder_2h', { 
              type: activityTitle
            })}
          </div>
        );
      case 'ACTIVITY_REMINDER_NEXT_DAY':
        return (
          <div className="notification-text clickable-activity">
            {t('types.activity_reminder_next_day', { 
              type: activityTitle
            })}
          </div>
        );
      case 'ACTIVITY_CANCELLED':
        return (
          <div className="notification-text clickable-activity">
            {t('types.activity_cancelled', { 
              type: activityTitle,
              author: sender?.name || t('fallback.organizer')
            })}
          </div>
        );
      case 'ONBOARDING':
        return t('types.onboarding');
      case 'PUSH_ACTIVATION':
        return (
          <div className="push-notification-content">
            <div className="notification-text">
              {t('types.push.title')}
            </div>
            <button 
              type="button"
              className="enable-push-button"
              onClick={(e) => {
                e.stopPropagation(); // Empêcher que le onClick parent soit déclenché
                onEnablePush?.();
              }}
            >
              {t('types.push.button')}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const buildNotificationClasses = () => {
    const classes = ['notification-item'];
    
    if (!read) classes.push('unread');
    if (notificationConfig.isSystem) classes.push('system');
    if (notificationConfig.isActivity && activity?.id) classes.push('activity-notification');
    if (notificationConfig.isFollowRequest) classes.push('follow-request');
    if ((notificationConfig.isActivity && activity?.id) || notificationConfig.isFollowRequest || type === 'ONBOARDING') {
      classes.push('clickable');
    }
    
    return classes.join(' ');
  };

  return (
    <div 
      className={buildNotificationClasses()}
      role="article"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex="0"
    >
      <div className="notification-icon">
        {renderIcon()}
      </div>
      <div className="notification-content">
        {sender && (
          <ClickableAvatar 
            user={sender}
            size="sm"
            className="notification-avatar"
          />
        )}
        <div className="notification-text">
          {renderContent()}
          <div className="notification-time">
            {formatRelativeDate(createdAt)}
          </div>
        </div>
      </div>
      {notificationConfig.isActivity && activity?.id && (
        <div className="notification-action">
          <Icon name="chevron-right" size={16} />
        </div>
      )}
    </div>
  );
};

export default NotificationItem; 